import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiRequest } from "../../api/http";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchEmployeeDetail } from "../../store/slices/employeesSlice";
import { EmployeeContractResponse, EmployeeContract, EmployeeContractDetail } from "../../types/admin";
import { formatDate, formatTemplateVersion } from "../../utils/admin";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import ContractViewer from "./components/ContractViewer";

const EmployeeContractDetailPage = () => {
  const { employeeId, contractId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, loadingDetail, detailError } = useAppSelector((state) => state.employees);

  const [allContracts, setAllContracts] = useState<EmployeeContract[]>([]);
  const [contract, setContract] = useState<EmployeeContractDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employeeId && (!detail || detail.employee_id !== Number(employeeId))) {
      dispatch(fetchEmployeeDetail(Number(employeeId)));
    }
  }, [dispatch, employeeId, detail]);

  useEffect(() => {
    const fetchContract = async () => {
      if (!detail?.branch_id || !employeeId || !contractId) return;
      
      setLoading(true);
      setError(null);
      try {
        // Fetch list of contracts for the dropdown
        const listRes = await apiRequest<EmployeeContractResponse>(`/admin/branches/${detail.branch_id}/employees/${employeeId}/contracts`);
        setAllContracts(listRes.items);
        
        // Fetch detailed contract info
        const detailRes = await apiRequest<EmployeeContractDetail>(`/admin/branches/${detail.branch_id}/employees/${employeeId}/contracts/${contractId}`);
        if (detailRes) {
          setContract(detailRes);
        } else {
          setError("해당 계약서를 찾을 수 없습니다.");
        }
      } catch (err: any) {
        setError(err.message || "계약서 정보를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    if (detail) {
      fetchContract();
    }
  }, [detail, employeeId, contractId]);

  return (
    <section className="admin-page" style={{ maxWidth: '100%', padding: '0 48px' }}>
      <header className="detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <button 
            className="back-btn" 
            type="button" 
            onClick={() => navigate(-1)}
          >
            ←
          </button>
          
          {contract && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <select
                value={contract.contract_id}
                onChange={(e) => navigate(`/admin/employees/${employeeId}/contracts/${e.target.value}`)}
                style={{
                  padding: '0 24px 0 0',
                  borderRadius: '0',
                  border: 'none',
                  backgroundColor: 'transparent',
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1d1d1f',
                  cursor: 'pointer',
                  margin: 0,
                  height: 'auto',
                  backgroundPosition: 'right center',
                  lineHeight: 1.2
                }}
              >
                {allContracts.map(c => (
                  <option key={c.contract_id} value={c.contract_id}>
                    {c.title}
                  </option>
                ))}
              </select>
              
              <div style={{ display: 'flex', gap: '16px', color: '#666874', fontSize: '13px' }}>
                <span><strong>유형:</strong> {formatTemplateVersion(contract.template_version)}</span>
                <span><strong>작성일자:</strong> {formatDate(contract.created_at)}</span>
                <span><strong>상태:</strong> {contract.status === 'completed' ? '완료' : contract.status === 'draft' ? '진행중' : contract.status}</span>
              </div>
            </div>
          )}
        </div>
        
        {contract && (
          <button
            type="button"
            onClick={async () => {
              if (contract.contract_file_url) {
                window.open(contract.contract_file_url, '_blank');
                return;
              }
              const element = document.getElementById("pdf-content");
              if (!element) {
                alert("PDF로 변환할 계약서 내용이 없습니다.");
                return;
              }
              try {
                const canvas = await html2canvas(element, { scale: 2 });
                const imgData = canvas.toDataURL("image/png");
                const pdf = new jsPDF("p", "mm", "a4");
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
                pdf.save(`${contract.title || "계약서"}.pdf`);
              } catch (err) {
                console.error("PDF 생성 실패", err);
                alert("PDF 생성에 실패했습니다.");
              }
            }}
            style={{
              backgroundColor: '#1d1d1f', color: 'white', padding: '10px 16px', borderRadius: '8px',
              fontWeight: '500', fontSize: '14px', cursor: 'pointer', border: 'none'
            }}
          >
            PDF 다운로드
          </button>
        )}
      </header>

      {loadingDetail && <p className="loading-text">직원 정보를 불러오는 중입니다...</p>}
      {detailError && <p className="error-text">{detailError}</p>}
      {loading && <p className="loading-text">데이터를 불러오는 중입니다...</p>}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 200px)', color: '#ff4d4f' }}>
          <p>{error}</p>
        </div>
      )}
      
      {!loadingDetail && !detailError && !loading && !error && contract && (
        <div style={{ 
          border: '1px solid #e7e8ef', 
          borderRadius: '12px', 
          overflow: 'hidden', 
          height: 'calc(100vh - 200px)',
          backgroundColor: 'white'
        }}>
          {contract.contract_file_url ? (
            <iframe
              src={`${contract.contract_file_url}#toolbar=0`}
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block' }}
              title="Contract PDF Viewer"
            />
          ) : contract.form_values && Object.keys(contract.form_values).length > 0 ? (
            <ContractViewer contract={contract} />
          ) : contract.document_preview_text ? (
            <div style={{ padding: '24px', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.6', overflowY: 'auto', height: '100%' }}>
              {contract.document_preview_text}
            </div>
          ) : contract.uploaded_files && contract.uploaded_files.length > 0 ? (
            <iframe
              src={`${contract.uploaded_files[0].file_url}#toolbar=0`}
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block' }}
              title="Contract PDF Viewer"
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666874' }}>
              <p>해당 계약서를 찾을 수 없습니다.</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default EmployeeContractDetailPage;
